import { useState } from 'react';
import './accounting.css';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useDeleteFlow } from '../../components/confirm';
import { FormTitle } from '../../components/FormTitle';
import { DataTable, type DTColumn } from './DataTable';
import { useFetchMs } from '../../hooks/useFetchMs';
import {
  usePaymentTerms, useSavePaymentTerm, useDeletePaymentTerm, errMessage, type PaymentTerm,
} from './accounting';

interface Props { onHome?: () => void; }

const COLUMNS: DTColumn<PaymentTerm>[] = [
  { key: 'terms', label: 'Terms', value: (r) => `${r.terms} days` },
];

/** Settings ▸ Utility ▸ Accounting ▸ Payment Terms — editable master list. */
export function PaymentTermsPage({ onHome }: Props) {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const runDelete = useDeleteFlow();
  const { data: rows = [], isFetching, refetch } = usePaymentTerms();
  const loadMs = useFetchMs(isFetching);
  const save = useSavePaymentTerm();
  const del = useDeletePaymentTerm();

  const [editing, setEditing] = useState<PaymentTerm | null | undefined>(undefined); // undefined = closed
  const [value, setValue] = useState('');
  const [valueError, setValueError] = useState('');

  const openNew = () => { setEditing(null); setValue(''); setValueError(''); };
  const openEdit = (r: PaymentTerm) => { setEditing(r); setValue(String(r.terms)); setValueError(''); };
  const close = () => setEditing(undefined);

  const submit = async () => {
    const n = Number(value);
    if (!value.trim() || !Number.isInteger(n) || n <= 0) {
      setValueError('Enter a whole number of days greater than 0.');
      return;
    }
    setValueError('');
    // Shared ALPACA-style save flow: "Do you want to Save?" → save via the
    // existing mutation → "Saved successfully." → close the modal.
    await runSave({
      save: async () => { await save.mutateAsync({ id: editing?.id, terms: n }); },
      successMessage: 'Payment term saved successfully.',
      onSaved: () => close(),
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  // Shared ALPACA-style delete flow: centred danger confirm → delete via the
  // existing mutation → "Deleted." acknowledgement (was a native window.confirm
  // with no success message — standardised 2026-07-03).
  const remove = async (r: PaymentTerm) => {
    await runDelete({
      message: `Delete payment term "${r.terms} days"?`,
      delete: async () => { await del.mutateAsync(r.id); },
      onError: (e) => notify.error(errMessage(e)),
    });
  };

  return (
    <div className="acc-page">
      <div className="acc-top">
        <div className="acc-bc">
          <a onClick={onHome}>Home</a><i className="ti ti-chevron-right" />
          <span>Settings</span><i className="ti ti-chevron-right" />
          <span>Utility</span><i className="ti ti-chevron-right" />
          <span>Accounting</span><i className="ti ti-chevron-right" />
          <span style={{ color: '#4a3728', fontWeight: 600 }}>Payment Terms</span>
        </div>
        {/* BO UI Standard v2: shared FormTitle heading (nav icon tile + title +
            the SINGLE top load-time chip) ON the grey page, then "N items". */}
        <FormTitle pageId="acc-paymentterms" auditFormName="Payment Terms" loadMs={loadMs} loading={isFetching} />
        <div className="acc-sub">{rows.length} item{rows.length === 1 ? '' : 's'}</div>
      </div>

      {/* Toolbar row · search row · ONE white grid card (composed by DataTable). */}
      <DataTable exportName="PaymentTerms"
        columns={COLUMNS} rows={rows} getId={(r) => r.id} loading={isFetching}
        emptyText="No payment terms yet — click New to add one."
        onNew={openNew} onEdit={openEdit} onDelete={remove}
        onRefresh={() => void refetch()} />

      {editing !== undefined && (
        <div className="acc-overlay" onMouseDown={close}>
          <div className="acc-dialog" onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{editing ? 'Edit Payment Term' : 'New Payment Term'}</span>
              <i className="ti ti-x acc-dialog-x" onClick={close} />
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field">
                <label className="acc-label">Terms (days)<span className="req">*</span></label>
                <input className="acc-input" type="number" min={1} step={1} autoFocus value={value}
                  onChange={(e) => { setValue(e.target.value); if (valueError) setValueError(''); }}
                  onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} />
                {valueError && <div className="acc-err">{valueError}</div>}
              </div>
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
              <button className="ca-btn ca-btn-solid" onClick={submit} disabled={save.isPending}>
                <span className="tb-i">💾</span> {save.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
